import { supabase } from '../lib/supabase';
import { orgStore } from '../lib/orgStore';

export interface Project {
  id: string;
  org_id: string;
  name: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

function requireOrgId(): string {
  const orgId = orgStore.getOrgId();
  if (!orgId) throw new Error('No organization found.');
  return orgId;
}

async function requireUserId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.id) throw new Error('You must be signed in to perform this action.');
  return user.id;
}

// Cache is keyed by org so switching orgs can never serve another org's list.
let _projectsCache: Project[] | null = null;
let _projectsCacheExpiry = 0;
let _projectsCacheOrgId: string | null = null;

export function invalidateProjectsCache() {
  _projectsCache = null;
  _projectsCacheExpiry = 0;
  _projectsCacheOrgId = null;
}

export const projectService = {
  // RLS returns only the projects the caller may see (admins → all org
  // projects; members → projects they belong to). The explicit org filter is
  // defense in depth and makes the query correct even before RLS is consulted.
  //
  // Throws when no org is loaded yet. Callers must wait for currentOrg rather
  // than calling on mount — an empty result would otherwise be cached for two
  // minutes and the switcher would look permanently empty.
  async getProjects(): Promise<Project[]> {
    const orgId = requireOrgId();
    const now = Date.now();

    if (_projectsCache && now < _projectsCacheExpiry && _projectsCacheOrgId === orgId) {
      return _projectsCache;
    }

    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false });
    if (error) throw error;

    const rows = (data ?? []) as Project[];
    _projectsCache = rows;
    _projectsCacheExpiry = now + 120_000;
    _projectsCacheOrgId = orgId;
    return rows;
  },

  async createProject(name: string, memberUserIds: string[] = []): Promise<Project> {
    const created_by = await requireUserId();
    const { data: project, error } = await supabase
      .from('projects')
      .insert({ org_id: requireOrgId(), name, created_by })
      .select()
      .single();
    if (error) throw error;

    // Add the creator + any selected members to project_members.
    const memberIds = Array.from(new Set([created_by, ...memberUserIds] as string[]));
    const rows = memberIds.map(user_id => ({ project_id: (project as Project).id, user_id, added_by: created_by }));
    const { error: memberErr } = await supabase.from('project_members').insert(rows);
    if (memberErr) console.error('[createProject] member insert failed:', memberErr);

    invalidateProjectsCache();
    return project as Project;
  },

  async getProjectMembers(projectId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('project_members')
      .select('user_id')
      .eq('project_id', projectId);
    if (error) throw error;
    return (data ?? []).map((m: { user_id: string }) => m.user_id);
  },

  async updateProject(projectId: string, updates: { name?: string; memberUserIds?: string[] }): Promise<Project> {
    if (updates.name !== undefined) {
      const { error } = await supabase
        .from('projects')
        .update({ name: updates.name, updated_at: new Date().toISOString() })
        .eq('id', projectId)
        .eq('org_id', requireOrgId());
      if (error) throw error;
    }

    // Reconcile members if provided.
    if (updates.memberUserIds !== undefined) {
      const addedBy = await requireUserId();
      const desired = new Set(updates.memberUserIds);
      const current = new Set(await projectService.getProjectMembers(projectId));

      const toAdd = Array.from(desired).filter(id => !current.has(id));
      const toRemove = Array.from(current).filter(id => !desired.has(id));

      if (toAdd.length) {
        const rows = toAdd.map(user_id => ({ project_id: projectId, user_id, added_by: addedBy }));
        const { error } = await supabase.from('project_members').insert(rows);
        if (error) throw error;
      }
      if (toRemove.length) {
        const { error } = await supabase
          .from('project_members')
          .delete()
          .eq('project_id', projectId)
          .in('user_id', toRemove);
        if (error) throw error;
      }
    }

    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .eq('org_id', requireOrgId())
      .single();
    if (error) throw error;
    invalidateProjectsCache();
    return data as Project;
  },
};
